import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, Inbox } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { leaveBalancesApi, leaveRequestsApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { LeaveRequest } from '@/types/hr';
import { formatDate, orDash } from '@/utils/format';
import { Days, LeaveStatusBadge, leaveYearOf } from './shared';
import { LeaveRequestDialog } from './LeaveRequestDialog';
import { LeaveDecisionDialog } from './LeaveDecisionDialog';

/** Evidence can be attached only while a request awaits a decision. */
const AWAITING_DECISION: LeaveRequest['status'][] = ['SUBMITTED', 'MANAGER_APPROVED'];

/**
 * An employee's own leave.
 *
 * <p>Everything on this page resolves the employee from the signed-in account —
 * there is no identifier in any request, so there is nothing to tamper with and
 * no way to read somebody else's.
 *
 * <p>The approval queue appears only for people who actually supervise somebody.
 * It is scoped to their own direct reports by the server, so a teacher who
 * supervises nobody simply sees nothing rather than an empty box.
 */
export function MyLeavePage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('startDate,desc');

  const [year] = useState(leaveYearOf());
  const [filing, setFiling] = useState(false);
  const [deciding, setDeciding] = useState<LeaveRequest | null>(null);
  const [attaching, setAttaching] = useState<LeaveRequest | null>(null);
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [confirmingReplace, setConfirmingReplace] = useState(false);

  const balances = useQuery({
    queryKey: queryKeys.leaveBalances.mine(year),
    queryFn: () => leaveBalancesApi.mine(year),
  });

  const params = table.params;
  const mine = useQuery({
    queryKey: queryKeys.leaveRequests.mine(params),
    queryFn: () => leaveRequestsApi.mine(params),
  });

  const queue = useQuery({
    queryKey: queryKeys.leaveRequests.awaitingMe,
    queryFn: () => leaveRequestsApi.awaitingMe(),
    enabled: can(PERMISSIONS.LEAVE_REQUEST_APPROVE),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.leaveRequests.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.leaveBalances.all });
  };

  const withdraw = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      leaveRequestsApi.cancel(id, reason),
    onSuccess: (request) => {
      toast.success(`${request.requestNumber} withdrawn`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  function openAttach(request: LeaveRequest) {
    setEvidenceFile(null);
    setConfirmingReplace(false);
    setAttaching(request);
  }

  function closeAttach() {
    setAttaching(null);
    setConfirmingReplace(false);
  }

  const attach = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      leaveRequestsApi.attachDocument(id, file),
    onSuccess: (request) => {
      toast.success(`Document attached to ${request.requestNumber}`);
      closeAttach();
      invalidate();
    },
    onError: (error) => {
      setConfirmingReplace(false);
      handleApiError(error);
    },
  });

  const downloadEvidence = useMutation({
    mutationFn: (request: LeaveRequest) =>
      leaveRequestsApi.downloadDocument(request.id, request.documentFilename),
    onError: (error) => handleApiError(error),
  });

  const columns: Column<LeaveRequest>[] = [
    {
      key: 'requestNumber',
      header: 'Request',
      render: (row) => (
        <span>
          <span className="block font-mono text-[12px] text-ink">{row.requestNumber}</span>
          <span className="block text-[11px] text-subtle">{orDash(row.leaveTypeName)}</span>
        </span>
      ),
    },
    {
      key: 'dates',
      header: 'Dates',
      sortKey: 'startDate',
      render: (row) => (
        <span className="text-sm text-ink">
          {formatDate(row.startDate)}
          {row.startDate !== row.endDate && <> → {formatDate(row.endDate)}</>}
        </span>
      ),
    },
    {
      key: 'days',
      header: 'Days',
      className: 'text-right',
      render: (row) => <Days value={row.days} />,
    },
    { key: 'status', header: 'Status', render: (row) => <LeaveStatusBadge status={row.status} /> },
    {
      key: 'note',
      header: 'Decision',
      render: (row) =>
        row.status === 'REJECTED' ? (
          <span className="text-[13px] text-muted">{row.hrComment ?? row.managerComment}</span>
        ) : row.status === 'CANCELLED' ? (
          <span className="text-[13px] text-muted">{row.cancellationReason}</span>
        ) : (
          <span className="text-subtle">—</span>
        ),
    },
    {
      key: 'evidence',
      header: 'Evidence',
      render: (row) => {
        const hasDocument = row.documentFilename !== undefined;
        const canAttach = AWAITING_DECISION.includes(row.status);
        if (!hasDocument && !canAttach) {
          return <span className="text-subtle">—</span>;
        }
        return (
          <span className="flex flex-wrap items-center gap-1.5">
            {hasDocument && (
              <Button
                size="sm"
                variant="ghost"
                title={row.documentFilename}
                loading={downloadEvidence.isPending && downloadEvidence.variables?.id === row.id}
                onClick={() => downloadEvidence.mutate(row)}
              >
                <span className="max-w-[10rem] truncate">{row.documentFilename}</span>
              </Button>
            )}
            {canAttach && (
              <Button size="sm" variant="secondary" onClick={() => openAttach(row)}>
                {hasDocument ? 'Replace' : 'Attach'}
              </Button>
            )}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        !['CANCELLED', 'REJECTED', 'TAKEN'].includes(row.status) &&
        new Date(row.startDate) > new Date() ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const reason = window.prompt('Why are you withdrawing this request?');
              if (reason !== null && reason.trim() !== '') {
                withdraw.mutate({ id: row.id, reason: reason.trim() });
              }
            }}
          >
            Withdraw
          </Button>
        ) : null,
    },
  ];

  const pending = queue.data ?? [];

  return (
    <div>
      <PageHeader
        title="My Leave"
        description="What you have left, what you have asked for, and what is waiting on you."
        icon={<CalendarCheck className="h-5 w-5" aria-hidden="true" />}
        action={<Button onClick={() => setFiling(true)}>Request leave</Button>}
      />

      <Card className="p-4">
        <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
          Balances for {year}
        </h2>
        {balances.isError ? (
          <p className="mt-3 text-[13px] text-muted">
            Your account is not linked to an employee record, so it has no leave. Ask HR to link it.
          </p>
        ) : (
          <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {(balances.data ?? []).map((balance) => (
              <div key={balance.id} className="border border-line p-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">
                  {balance.leaveTypeName}
                  {!balance.paid && <span className="ml-1 normal-case text-subtle">(unpaid)</span>}
                </p>
                <p className="mt-1 text-2xl font-extrabold leading-none text-ink">
                  <Days value={balance.remainingDays} />
                </p>
                <p className="mt-1 text-[11px] text-muted">
                  of <Days value={balance.entitledDays + balance.carriedForwardDays} /> ·{' '}
                  <Days value={balance.takenDays} /> taken
                  {balance.pendingDays > 0 && (
                    <>
                      {' '}
                      · <Days value={balance.pendingDays} /> pending
                    </>
                  )}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {can(PERMISSIONS.LEAVE_REQUEST_APPROVE) && pending.length > 0 && (
        <Card className="mt-3 p-4">
          <h2 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            <Inbox className="h-4 w-4" aria-hidden="true" />
            Waiting on you ({pending.length})
          </h2>
          <ul className="mt-2">
            {pending.map((request) => (
              <li
                key={request.id}
                className="flex flex-wrap items-center justify-between gap-3 border-b border-line py-2.5 last:border-0"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-ink">
                    {request.employeeName} · {request.leaveTypeName}
                  </span>
                  <span className="block text-[12px] text-muted">
                    {formatDate(request.startDate)}
                    {request.startDate !== request.endDate && (
                      <> → {formatDate(request.endDate)}</>
                    )}{' '}
                    · <Days value={request.days} /> days · {request.reason}
                  </span>
                </span>
                <Button size="sm" variant="secondary" onClick={() => setDeciding(request)}>
                  Decide
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="mt-3 overflow-hidden">
        <div className="border-b-2 border-line px-4 py-3">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            My requests
          </h2>
        </div>
        <DataTable
          columns={columns}
          rows={mine.data?.data}
          rowKey={(row) => row.id}
          loading={mine.isPending}
          error={mine.error}
          onRetry={() => void mine.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No leave requests"
          emptyDescription="You have not asked for any leave yet."
        />
      </Card>

      {filing && (
        <LeaveRequestDialog
          mode="SELF"
          onClose={() => setFiling(false)}
          onFiled={() => {
            setFiling(false);
            invalidate();
          }}
        />
      )}

      <Modal
        open={attaching !== null && !confirmingReplace}
        onClose={closeAttach}
        title={attaching === null ? '' : `Supporting document · ${attaching.requestNumber}`}
        description="A doctor's note, a summons — whatever supports the request. Only while it awaits a decision."
        footer={
          <>
            <Button variant="secondary" onClick={closeAttach} disabled={attach.isPending}>
              Cancel
            </Button>
            <Button
              loading={attach.isPending}
              disabled={evidenceFile === null}
              onClick={() => {
                if (attaching === null || evidenceFile === null) {
                  return;
                }
                if (attaching.documentFilename !== undefined) {
                  setConfirmingReplace(true);
                } else {
                  attach.mutate({ id: attaching.id, file: evidenceFile });
                }
              }}
            >
              Upload
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {attaching?.documentFilename !== undefined && (
            <p className="text-sm text-muted">
              Currently attached:{' '}
              <span className="break-all text-ink">{attaching.documentFilename}</span>. Uploading
              replaces it.
            </p>
          )}
          <div>
            <label htmlFor="leave-evidence-file" className="mb-1.5 block text-xs font-medium text-muted">
              File<span className="ml-0.5 text-danger">*</span>
            </label>
            <input
              id="leave-evidence-file"
              type="file"
              onChange={(event) => setEvidenceFile(event.target.files?.[0] ?? null)}
              className="block w-full text-sm text-muted file:mr-3 file:rounded-lg file:border-0 file:bg-surface-2 file:px-3 file:py-2 file:text-sm file:text-ink"
            />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={attaching !== null && confirmingReplace}
        destructive
        title={`Replace the document on ${attaching?.requestNumber ?? ''}?`}
        message={`${attaching?.documentFilename ?? 'The current document'} will be removed and ${
          evidenceFile?.name ?? 'the new file'
        } kept in its place.`}
        confirmLabel="Replace"
        loading={attach.isPending}
        onCancel={() => setConfirmingReplace(false)}
        onConfirm={() => {
          if (attaching !== null && evidenceFile !== null) {
            attach.mutate({ id: attaching.id, file: evidenceFile });
          }
        }}
      />

      {deciding !== null && (
        <LeaveDecisionDialog
          request={deciding}
          stage="MANAGER"
          onClose={() => setDeciding(null)}
          onDecided={() => {
            setDeciding(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}
